import { useState, useEffect, useMemo } from 'react';
import { X, Calendar, Repeat } from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useTranslation } from '@/hooks/useTranslation';
import { useCategories } from '@/hooks/useCategories';
import { CustomSelect } from '@/components/ui/custom-select';
import { useAccounts } from '@/hooks/useAccounts';
import type {
  RecurringTransaction,
  RecurringTransactionInsert,
  Frequency,
} from '@/hooks/useRecurringTransactions';

interface RecurringTransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: RecurringTransactionInsert) => Promise<{ success: boolean; error?: string }>;
  recurring?: RecurringTransaction | null;
}

const FREQUENCIES: Frequency[] = ['daily', 'weekly', 'monthly', 'yearly'];

function toLocalDateStr(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function RecurringTransactionModal({
  isOpen,
  onClose,
  onSave,
  recurring,
}: RecurringTransactionModalProps) {
  const { t } = useTranslation();
  const { getVisibleCategoriesGrouped, getCategoryIcon } = useCategories();
  const { accounts } = useAccounts();

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    description: '',
    amount: '',
    type: 'expense' as 'income' | 'expense',
    frequency: 'monthly' as Frequency,
    start_date: toLocalDateStr(new Date()),
    end_date: '',
    category_id: '',
    account_id: '',
  });

  useEffect(() => {
    if (recurring) {
      setFormData({
        description: recurring.description,
        amount: recurring.amount.toString(),
        type: recurring.type,
        frequency: recurring.frequency,
        start_date: recurring.start_date.split('T')[0],
        end_date: recurring.end_date ? recurring.end_date.split('T')[0] : '',
        category_id: recurring.category_id || '',
        account_id: recurring.account_id || '',
      });
    } else {
      setFormData({
        description: '',
        amount: '',
        type: 'expense',
        frequency: 'monthly',
        start_date: toLocalDateStr(new Date()),
        end_date: '',
        category_id: '',
        account_id: '',
      });
    }
  }, [recurring, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.description || !formData.amount || !formData.account_id) return;

    setLoading(true);
    const result = await onSave({
      description: formData.description,
      amount: parseFloat(formData.amount.replace(',', '.')),
      type: formData.type,
      frequency: formData.frequency,
      start_date: formData.start_date,
      end_date: formData.end_date || null,
      category_id: formData.category_id || null,
      account_id: formData.account_id,
    });
    setLoading(false);

    if (result.success) {
      onClose();
    }
  };

  const groupedCategories = useMemo(() => {
    return getVisibleCategoriesGrouped(formData.type);
  }, [formData.type, getVisibleCategoriesGrouped]);
  // Synchronised accounts are left out on purpose: the bank already reports
  // the rent, the subscription, the salary. A rule on such an account would
  // produce a second operation next to the real one, every month.
  const activeAccounts = accounts.filter((a) => !a.is_hidden && a.source !== 'powens');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-500/10 rounded-xl flex items-center justify-center">
              <Repeat className="w-5 h-5 text-purple-500" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {recurring ? t('recurring.edit') : t('recurring.create')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Type selector */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setFormData({ ...formData, type: 'expense', category_id: '' })}
              className={`flex-1 py-2.5 rounded-xl font-medium transition-all ${
                formData.type === 'expense'
                  ? 'bg-red-500 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
              }`}
            >
              {t('recurring.expense')}
            </button>
            <button
              type="button"
              onClick={() => setFormData({ ...formData, type: 'income', category_id: '' })}
              className={`flex-1 py-2.5 rounded-xl font-medium transition-all ${
                formData.type === 'income'
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
              }`}
            >
              {t('recurring.income')}
            </button>
          </div>

          {/* Description */}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1.5">
              {t('recurring.description')}
            </label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder={t('recurring.descriptionPlaceholder')}
              className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500"
              required
            />
          </div>

          {/* Amount */}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1.5">
              {t('recurring.amount')}
            </label>
            <input
              type="text"
              inputMode="decimal"
              value={formData.amount}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9.,]/g, '');
                setFormData({ ...formData, amount: value });
              }}
              placeholder="0.00"
              className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500"
              required
            />
          </div>

          {/* Frequency */}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1.5">
              {t('recurring.frequency')}
            </label>
            <div className="grid grid-cols-4 gap-2">
              {FREQUENCIES.map((freq) => (
                <button
                  key={freq}
                  type="button"
                  onClick={() => setFormData({ ...formData, frequency: freq })}
                  className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                    formData.frequency === freq
                      ? 'bg-purple-500 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {t(`recurring.frequency.${freq}`)}
                </button>
              ))}
            </div>
          </div>

          {/* Start Date */}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1.5">
              {t('recurring.startDate')}
            </label>
            <div className="relative">
              <DatePicker
                selected={formData.start_date ? new Date(formData.start_date + 'T00:00:00') : null}
                onChange={(date) =>
                  setFormData({
                    ...formData,
                    start_date: date ? toLocalDateStr(date) : '',
                  })
                }
                dateFormat="dd/MM/yyyy"
                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#16c47f]/30 focus:border-[#16c47f]"
                wrapperClassName="w-full"
              />
              <Calendar className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* End Date (optional) */}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1.5">
              {t('recurring.endDate')}{' '}
              <span className="text-gray-400">({t('recurring.optional')})</span>
            </label>
            <div className="relative">
              <DatePicker
                selected={formData.end_date ? new Date(formData.end_date + 'T00:00:00') : null}
                onChange={(date) =>
                  setFormData({
                    ...formData,
                    end_date: date ? toLocalDateStr(date) : '',
                  })
                }
                dateFormat="dd/MM/yyyy"
                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#16c47f]/30 focus:border-[#16c47f]"
                wrapperClassName="w-full"
                isClearable
              />
              <Calendar className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Category */}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1.5">
              {t('recurring.category')}
            </label>
            <CustomSelect
              value={formData.category_id}
              onChange={(value) => setFormData({ ...formData, category_id: value })}
              placeholder={t('recurring.selectCategory')}
              options={groupedCategories.flatMap(({ group, categories }) =>
                categories.map((cat) => ({
                  value: cat.id,
                  label: `${getCategoryIcon(cat.icon)} ${cat.name}`,
                  group: group.name,
                }))
              )}
            />
          </div>

          {/* Account (required) */}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1.5">
              {t('recurring.account')} <span className="text-red-500">*</span>
            </label>
            <CustomSelect
              value={formData.account_id}
              onChange={(value) => setFormData({ ...formData, account_id: value })}
              required
              placeholder={t('recurring.selectAccount')}
              className={!formData.account_id ? 'border-red-300 dark:border-red-500' : ''}
              options={activeAccounts.map((acc) => ({
                value: acc.id,
                label: acc.name,
              }))}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 px-4 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              {t('recurring.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading || !formData.description || !formData.amount}
              className="flex-1 py-2.5 px-4 bg-purple-500 hover:bg-purple-600 text-white font-medium rounded-xl transition-colors disabled:opacity-50"
            >
              {loading ? t('recurring.saving') : t('recurring.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
